import { useRef } from "react";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Search from "../Search";
import { SectionPermissionTypeEnum } from "../../types/SectionPermissionTypesEnum";
import "./DocumentsListPanel.css";
import { DocumentsRowActions } from "./DocumentsRowActions";
import { DocumentPanelItem } from "./types";
import { getDocumentListKey } from "./documentUtils";


interface DocumentsListPanelProps<T extends DocumentPanelItem> {
    filteredDocuments: T[];
    loading: boolean;
    searchText: string;
    appliedSearch: string;
    onSearchChange: (text: string) => void;
    onSearch: () => void;
    onClearSearch: () => void;
    onLoadFiles?: (files: File[]) => void;
    onDelete?: (doc: T) => void;
    onStar?: (doc: T) => void;
    onPreview: (doc: T) => void;
    onInfo?: (doc: T) => void;
    onIcon?: (doc: T) => void;
    onDownload?: (doc: T) => void;
    sectionPermission?: SectionPermissionTypeEnum;
    disabledActions: boolean;
}

export default function DocumentsListPanel<T extends DocumentPanelItem>({
    filteredDocuments, loading, searchText, appliedSearch,
    onSearchChange, onSearch, onClearSearch, onLoadFiles,
    onDelete, onStar, onPreview, onInfo, onIcon, onDownload,
    sectionPermission = SectionPermissionTypeEnum.CLAIMS, disabledActions
}: DocumentsListPanelProps<T>) {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    return (
        <div className="documents-list-panel d-flex flex-column h-100">
            <div className="documents-list-search">
                <Search searchText={searchText} onChange={onSearchChange} onSearch={onSearch} onClear={onClearSearch} />
            </div>


            <div className="documents-list-body flex-grow-1">
                {filteredDocuments.length === 0 && !loading && (
                    <p className="text-center text-muted">
                        {appliedSearch ? t("common:documents.noDocumentsFound") : t("common:documents.noDocumentsFound")}
                    </p>
                )}
                <ul className="documents-list-ul">
                    {filteredDocuments.map((doc) => (
                        <li key={getDocumentListKey(doc)} className="documents-list-item">
                            <DocumentsRowActions<T>
                                object={doc}
                                onDelete={onDelete ? () => onDelete(doc) : undefined}
                                onInfo={onInfo ? () => onInfo(doc) : undefined}
                                onStar={onStar ? () => onStar(doc) : undefined}
                                onFileNameClick={() => onPreview(doc)}
                                onIcon={onIcon ? () => onIcon(doc) : undefined}
                                onDownload={onDownload ? () => onDownload(doc) : undefined}
                                t={t}
                                sectionPermission={sectionPermission}
                                canDelete={() => !disabledActions}
                                canStar={() => !disabledActions}
                            />
                        </li>
                    ))}
                </ul>
                 {onLoadFiles && (
                <div className="documents-list-upload">
                    <Button
                        variant="secondary"
                        disabled={disabledActions}
                        onClick={() => fileInputRef.current?.click()}
                        className="documents-list-upload-button"
                    >
                        {t("common:documents.loadDocumentButton")}
                    </Button>
                    <Form.Control
                        type="file"
                        ref={fileInputRef}
                        multiple
                        onChange={(e) => {
                            const files = Array.from((e.target as HTMLInputElement).files ?? []);
                            if (files.length > 0) onLoadFiles(files);
                            (e.target as HTMLInputElement).value = "";
                        }}
                        style={{ display: "none" }}
                    />
                </div>
            )}
            </div>
        </div>
    );
}
